import { PlainButton, MultiSectionLayout } from "@jugl-web/ui-kit";
import { FC, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { cx, useTranslations } from "@jugl-web/utils";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useUserAuth } from "@web-src/modules/auth/providers/UserAuthProvider/useUserAuth";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { ReactComponent as GeneralIcon } from "./assets/general.svg";
import { ReactComponent as PlansIcon } from "./assets/plans.svg";
import { ReactComponent as NotificationIcon } from "./assets/notification.svg";
import { ReactComponent as LogoutIcon } from "./assets/logout.svg";
import { ReactComponent as AdminIcon } from "./assets/admin.svg";
import { ReactComponent as TeamIcon } from "./assets/team.svg";
import { ReactComponent as UserIcon } from "./assets/user.svg";

interface CXWorkspaceSettingsSidebarProps {
  children?: ReactNode;
}

export const CXWorkspaceSettingsSidebar: FC<
  CXWorkspaceSettingsSidebarProps
> = ({ children }) => {
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const { logout } = useUserAuth();
  const { isAdmin } = useUserRole();

  return (
    <MultiSectionLayout
      className="h-[calc(100vh-108px)]"
      classNames={{
        leftSide:
          "w-[240px] overflow-hidden border-y-0 border-l-0 border-r border-solid border-r-[#E0E2E3] bg-white max-lg:w-14",
        middle: "min-w-0",
        content: "jugl__custom-scrollbar min-w-0 overflow-x-auto",
      }}
      leftSide={
        <aside className="flex h-full flex-col">
          <nav className="flex flex-1 flex-col gap-3 overflow-y-auto px-2 py-3 max-lg:gap-2 max-lg:px-1 max-lg:py-4">
            {isAdmin && (
              <>
                <NavItem
                  label={t({
                    id: "cx-workspace-settings.general",
                    defaultMessage: "General",
                  })}
                  icon={<GeneralIcon />}
                  onClick={() => navigateToPage("cxWorkspaceGeneral")}
                  pathSegment="general"
                />
                <NavItem
                  label={t({
                    id: "cx-workspace-settings.team",
                    defaultMessage: "Team",
                  })}
                  icon={<TeamIcon />}
                  onClick={() => navigateToPage("cxWorkspaceTeam")}
                  pathSegment="team"
                />
                <NavItem
                  label={t({
                    id: "cx-workspace-settings.plans",
                    defaultMessage: "Plans",
                  })}
                  icon={<PlansIcon />}
                  onClick={() => navigateToPage("cxWorkspacePlans")}
                  pathSegment="plans"
                />
              </>
            )}
            <NavItem
              label={t({
                id: "cx-workspace-settings.my-account",
                defaultMessage: "My Account",
              })}
              icon={<UserIcon />}
              onClick={() => navigateToPage("cxWorkspaceMyAccount")}
              pathSegment="my-account"
            />

            <NavItem
              label={t({
                id: "cx-workspace-settings.notifications-sidebar",
                defaultMessage: "Notifications",
              })}
              icon={<NotificationIcon />}
              onClick={() => navigateToPage("cxWorkspaceNotifications")}
              pathSegment="notifications"
            />

            <NavItem
              label={t({
                id: "cx-workspace-settings.privacy-policy",
                defaultMessage: "Privacy Policy",
              })}
              icon={<AdminIcon />}
              onClick={() =>
                window.open("https://jugl.com/privacy-policy", "_blank")
              }
            />

            <NavItem
              label={t({
                id: "cx-workspace-settings.logout",
                defaultMessage: "Log out",
              })}
              icon={<LogoutIcon />}
              onClick={() => logout(true)}
            />
          </nav>
        </aside>
      }
    >
      {children}
    </MultiSectionLayout>
  );
};

interface NavItemProps {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  pathSegment?: string;
}

const NavItem: FC<NavItemProps> = ({ label, icon, onClick, pathSegment }) => {
  const { pathname } = useLocation();
  const isActive = pathSegment ? pathname.includes(pathSegment) : false;

  return (
    <PlainButton
      onClick={onClick}
      title={label}
      className={cx(
        "flex h-12 w-full items-center gap-1 rounded-lg px-1 transition-colors max-lg:h-10 max-lg:justify-center max-lg:gap-0 max-lg:px-0",
        isActive
          ? "bg-[#F9F9F9] font-medium text-[#1A2023]"
          : "text-[#4F4F4F] hover:bg-[#F9F9F9]"
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center">
        {icon}
      </span>
      <span className="font-secondary text-sm max-lg:hidden">{label}</span>
    </PlainButton>
  );
};
